import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("qrmp_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => { setToken(null); setUser(false); })
      .finally(() => setLoading(false));
  }, []);

  // Listen for global "auth expired" events emitted by the axios interceptor.
  // When the backend returns 401 on any authenticated request, the interceptor
  // already wiped the localStorage token; here we clear the in-memory user
  // state so the UI immediately reflects the logged-out condition.
  useEffect(() => {
    const handler = () => { setUser(false); };
    window.addEventListener("qrmp:auth-expired", handler);
    return () => window.removeEventListener("qrmp:auth-expired", handler);
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setToken(data.access_token);
    setUser(data);
    // Refresh from /auth/me so we always have enriched fields (trial_active, etc.)
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {}
    return data;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    if (data.access_token) setToken(data.access_token);
    setUser(data);
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {}
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
